import { styled } from '@mui/material/styles';
import { LinearProgress, CircularProgress, Box } from '@mui/material';

// Helper to blend two hex colors (simple average)
function blendColors(color1: string, color2: string, weight = 0.7) {
  // Handle non-hex colors by returning the first color
  if (!color1.startsWith('#') || !color2.startsWith('#')) {
    return color1;
  }
  
  color1 = color1.replace('#', '');
  color2 = color2.replace('#', '');
  const r = Math.round(
    parseInt(color1.substring(0, 2), 16) * weight +
    parseInt(color2.substring(0, 2), 16) * (1 - weight)
  );
  const g = Math.round(
    parseInt(color1.substring(2, 4), 16) * weight +
    parseInt(color2.substring(2, 4), 16) * (1 - weight)
  );
  const b = Math.round(
    parseInt(color1.substring(4, 6), 16) * weight +
    parseInt(color2.substring(4, 6), 16) * (1 - weight)
  );
  return `#${r.toString(16).padStart(2, '0')}${g
    .toString(16)
    .padStart(2, '0')}${b.toString(16).padStart(2, '0')}`;
}

// Helper to check if color is a theme color
function isThemeColor(color: string): color is 'primary' | 'secondary' | 'success' | 'error' | 'warning' | 'info' | 'unicityBlue' | 'unicityGreen' | 'unicityPurple' | 'unicityOrange' | 'unicityNavy' {
  return ['primary', 'secondary', 'success', 'error', 'warning', 'info', 'unicityBlue', 'unicityGreen', 'unicityPurple', 'unicityOrange', 'unicityNavy'].includes(color);
}

interface StyledLinearProgressProps {
  size?: 'small' | 'medium' | 'large' | number;
  color?: 'primary' | 'secondary' | 'success' | 'error' | 'warning' | 'info' | 'unicityBlue' | 'unicityGreen' | 'unicityPurple' | 'unicityOrange' | 'unicityNavy' | string;
}

export const ProgressContainer = styled(Box)(({ theme: _theme }) => ({
  width: '100%',
}));

export const StyledLinearProgress = styled(LinearProgress, {
  shouldForwardProp: (prop) => prop !== 'size',
})<StyledLinearProgressProps>(({ theme, color, size = 'medium' }) => {
  // Calculate bar height based on size
  const getBarHeight = () => {
    if (typeof size === 'number') return size;
    
    switch (size) {
      case 'small':
        return 8;
      case 'large':
        return 16;
      default: // medium
        return 12;
    }
  };
  
  const barHeight = getBarHeight();
  const barRadius = barHeight / 2; // half the height for full half-circle

  // Get main and light color from theme or use custom color
  const mainColor = color && isThemeColor(color) && theme.palette[color]?.main 
    ? theme.palette[color]!.main 
    : color || '#48C774';
  
  // Dark mode and accessibility mode detection
  const isDarkMode = theme.palette.mode === 'dark';
  const isNeumorphicDark = theme.palette.background.default === '#1a1a1a' && theme.palette.mode === 'dark';
  const isHighContrast = theme.palette.background.default === '#ffffff' && theme.palette.primary.main === '#000000' ||
                        theme.palette.background.default === '#000000' && theme.palette.primary.main === '#ffffff';
  
  // Use high contrast background colors for better accessibility
  let backgroundColor: string;
  if (isHighContrast) {
    // High contrast mode uses gray backgrounds for better contrast
    backgroundColor = isDarkMode ? '#333333' : '#e0e0e0';
  } else {
    // Standard color logic for non-high-contrast modes
    let veryLightColor: string;
    if (color && isThemeColor(color) && theme.palette[color]?.main) {
      veryLightColor = theme.palette[color].main + '22'; // add alpha for extra lightness
    } else if (color && color.startsWith('#')) {
      veryLightColor = color + '22'; // add alpha for custom hex colors
    } else if (color && (color.startsWith('rgb') || color.startsWith('hsl'))) {
      // For rgb/rgba/hsl colors, use a light gray fallback
      veryLightColor = '#f0f0f0';
    } else {
      veryLightColor = '#EAFBF2';
    }
    
    backgroundColor = veryLightColor;
    if (isDarkMode) {
      if (color && isThemeColor(color) && theme.palette[color]?.main) {
        backgroundColor = theme.palette[color].main + '15'; // darker alpha for dark mode
      } else if (color && color.startsWith('#')) {
        backgroundColor = color + '15'; // darker alpha for custom hex colors
      } else {
        backgroundColor = '#2a2a2a';
      }
    }
    if (isNeumorphicDark) {
      backgroundColor = '#2a2a2a';
    }
  }

  const lightColor = color && isThemeColor(color) && theme.palette[color]?.light 
    ? theme.palette[color].light 
    : mainColor;
  // Blend main and light color for a slightly darker wave
  const waveColor = blendColors(mainColor, lightColor, 0.7);

  // Neumorphic effects for dark neumorphic theme
  const neumorphicStyles = isNeumorphicDark ? {
    boxShadow: 'inset 2px 2px 5px #0a0a0a, inset -2px -2px 5px #2a2a2a',
    border: '1px solid #333333',
  } : {};

  return {
    height: barHeight,
    borderRadius: barRadius,
    backgroundColor,
    ...neumorphicStyles,
    '& .MuiLinearProgress-bar': {
      borderRadius: barRadius,
      backgroundColor: mainColor, // fallback solid
      backgroundImage: `linear-gradient(120deg, ${mainColor} 40%, ${waveColor} 50%, ${mainColor} 60%)`,
      backgroundSize: '200% 100%',
      animation: 'wave 2s linear infinite',
      // Add neumorphic glow effect for dark neumorphic theme
      ...(isNeumorphicDark && {
        boxShadow: `0 0 10px ${mainColor}40, inset 0 0 5px ${mainColor}20`,
      }),
    },
    '@keyframes wave': {
      '0%': { backgroundPosition: '200% 0' },
      '100%': { backgroundPosition: '-200% 0' },
    },
  };
});

export const StyledCircularProgress = styled(CircularProgress)(({ theme }) => {
  const isNeumorphicDark = theme.palette.background.default === '#1a1a1a' && theme.palette.mode === 'dark';
  
  return {
    '& .MuiCircularProgress-circle': {
      strokeLinecap: 'round',
      // Add neumorphic glow effect for dark neumorphic theme
      ...(isNeumorphicDark && {
        filter: 'drop-shadow(0 0 8px rgba(255, 255, 255, 0.3))',
      }),
    },
  };
});

export const StyledPillLabelProgress = styled(Box)<{
  value: number;
  label: string;
  color?: 'primary' | 'secondary' | 'success' | 'error' | 'warning' | 'info' | 'unicityBlue' | 'unicityGreen' | 'unicityPurple' | 'unicityOrange' | 'unicityNavy' | string;
}>(
  ({ theme, value: _value, label: _label, color = 'unicityNavy' }) => {
    const paletteEntry = isThemeColor(color) ? theme.palette[color as keyof typeof theme.palette] : null;
    
    // Theme-aware border and background colors with high contrast support
    const isDarkMode = theme.palette.mode === 'dark';
    const isNeumorphicDark = theme.palette.background.default === '#1a1a1a' && theme.palette.mode === 'dark';
    const isHighContrast = theme.palette.background.default === '#ffffff' && theme.palette.primary.main === '#000000' ||
                          theme.palette.background.default === '#000000' && theme.palette.primary.main === '#ffffff';
    
    let borderColor: string;
    let backgroundColor: string;
    
    if (isHighContrast) {
      // High contrast mode uses gray backgrounds for better contrast
      borderColor = isDarkMode ? '#666666' : '#999999';
      backgroundColor = isDarkMode ? '#333333' : '#e0e0e0';
    } else {
      // Standard color logic for non-high-contrast modes
      if (paletteEntry && typeof paletteEntry === 'object' && 'light' in paletteEntry && typeof paletteEntry.light === 'string') {
        borderColor = paletteEntry.light;
      } else if (color && !isThemeColor(color)) {
        // For custom colors, use a lighter version or fallback
        borderColor = color.startsWith('#') ? color + '40' : '#E9EEF3';
      } else {
        borderColor = '#E9EEF3';
      }
      backgroundColor = borderColor;
      
      if (isDarkMode) {
        borderColor = theme.palette.grey[700];
        backgroundColor = theme.palette.grey[800];
      }
      
      if (isNeumorphicDark) {
        borderColor = '#333333';
        backgroundColor = '#2a2a2a';
      }
    }

    const neumorphicStyles = isNeumorphicDark ? {
      boxShadow: 'inset 2px 2px 5px #0a0a0a, inset -2px -2px 5px #2a2a2a',
      border: '1px solid #333333',
    } : {};

    return {
      width: '100%',
      height: 24,
      borderRadius: 12, // half the height for full half-circle
      background: backgroundColor,
      position: 'relative',
      overflow: 'hidden',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      ...neumorphicStyles,
    };
  }
);

export const StyledPillLabelFill = styled(Box)<{
  value: number;
  color?: 'primary' | 'secondary' | 'success' | 'error' | 'warning' | 'info' | 'unicityBlue' | 'unicityGreen' | 'unicityPurple' | 'unicityOrange' | 'unicityNavy' | string;
}>(
  ({ theme, value, color = 'unicityNavy' }) => {
    const paletteEntry = isThemeColor(color) ? theme.palette[color as keyof typeof theme.palette] : null;
    const mainColor = paletteEntry && typeof paletteEntry === 'object' && 'main' in paletteEntry
      ? paletteEntry.main
      : color || '#183A5A';
    
    const isNeumorphicDark = theme.palette.background.default === '#1a1a1a' && theme.palette.mode === 'dark';
    
    const neumorphicStyles = isNeumorphicDark ? {
      boxShadow: `inset 0 0 10px ${mainColor}40`,
    } : {};

    return {
      position: 'absolute',
      left: 0,
      top: 0,
      height: '100%',
      width: `${value}%`,
      background: mainColor,
      borderRadius: 12, // half the height for full half-circle
      overflow: 'hidden',
      zIndex: 1,
      pointerEvents: 'none',
      ...neumorphicStyles,
    };
  }
);

export const StyledPillLabelTextSplit = styled(Box)<{
  value: number;
  color?: 'primary' | 'secondary' | 'success' | 'error' | 'warning' | 'info' | 'unicityBlue' | 'unicityGreen' | 'unicityPurple' | 'unicityOrange' | 'unicityNavy' | string;
  contrast: 'light' | 'dark';
}>(
  ({ theme, value, color = 'unicityNavy', contrast }) => {
    const paletteEntry = isThemeColor(color) ? theme.palette[color as keyof typeof theme.palette] : null;
    const mainColor = paletteEntry && typeof paletteEntry === 'object' && 'main' in paletteEntry
      ? paletteEntry.main
      : color || '#183A5A';
    
    const isDarkMode = theme.palette.mode === 'dark';
    const isNeumorphicDark = theme.palette.background.default === '#1a1a1a' && theme.palette.mode === 'dark';
    
    // Theme-aware text colors
    let textColor = contrast === 'light' ? '#fff' : mainColor;
    
    if (isDarkMode) {
      if (contrast === 'light') {
        textColor = '#fff';
      } else {
        textColor = theme.palette.text.primary;
      }
    }
    
    if (isNeumorphicDark) {
      if (contrast === 'light') {
        textColor = '#fff';
      } else {
        textColor = '#c1c2c8';
      }
    }

    return {
      position: 'absolute',
      left: 0,
      top: 0,
      width: '100%',
      height: '100%',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      fontWeight: 500,
      fontSize: 11,
      zIndex: 2,
      pointerEvents: 'none',
      whiteSpace: 'nowrap',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      padding: '0 12px',
      color: textColor,
      transition: 'color 0.3s',
      clipPath: contrast === 'light'
        ? `polygon(0 0, ${value}% 0, ${value}% 100%, 0 100%)` // white label: only show up to fill
        : `polygon(${value}% 0, 100% 0, 100% 100%, ${value}% 100%)`, // navy label: only show after fill
    };
  }
); 